import React, { type PropsWithChildren, useCallback, useMemo } from 'react';

import { type AuthActions, AuthActionsContext } from './AuthActionsContext';
import type { TrackError } from './helpers';
import {
  logIn as logInRequest,
  register as registerRequest,
  requestHumanStatus as requestHumanStatusRequest,
  requestPasswordReset as requestPasswordResetRequest,
} from './sessionClient';

export type AuthActionsProviderProps = PropsWithChildren<{
  authBaseUrl: string;
  analyticsPrefix: string;
  fetch?: typeof fetch;
  trackError?: TrackError;
  recordMarketingSmsConsent: AuthActions['recordMarketingSmsConsent'];
}>;

export function useAuthActionsValue({
  authBaseUrl,
  analyticsPrefix,
  fetch: fetchFn = fetch,
  trackError,
  recordMarketingSmsConsent: writeMarketingSmsConsent,
}: Omit<AuthActionsProviderProps, 'children'>): AuthActions {
  const resolvedAuthBaseUrl = authBaseUrl.replace(/\/+$/, '');
  const logIn: AuthActions['logIn'] = useCallback(
    (credentials) => logInRequest(resolvedAuthBaseUrl, credentials, fetchFn),
    [fetchFn, resolvedAuthBaseUrl],
  );
  const register: AuthActions['register'] = useCallback(
    (params) => registerRequest(resolvedAuthBaseUrl, params, fetchFn),
    [fetchFn, resolvedAuthBaseUrl],
  );
  const requestHumanStatus: AuthActions['requestHumanStatus'] = useCallback(
    (email) => requestHumanStatusRequest(resolvedAuthBaseUrl, email, fetchFn),
    [fetchFn, resolvedAuthBaseUrl],
  );
  const requestPasswordReset: AuthActions['requestPasswordReset'] = useCallback(
    (email) => requestPasswordResetRequest(resolvedAuthBaseUrl, email, fetchFn),
    [fetchFn, resolvedAuthBaseUrl],
  );
  const recordMarketingSmsConsent: AuthActions['recordMarketingSmsConsent'] = useCallback(
    async (consent) => {
      let result: Awaited<ReturnType<AuthActions['recordMarketingSmsConsent']>>;
      try {
        result = await writeMarketingSmsConsent(consent);
      } catch {
        result = { success: false };
      }
      if (!result.success) {
        // Failed opt-outs need an operational signal even though consent is
        // best-effort and must not block a completed registration.
        trackError?.('marketing-sms-consent', new Error('marketing SMS consent write failed'));
      }
      return result;
    },
    [trackError, writeMarketingSmsConsent],
  );

  return useMemo(
    () => ({
      analyticsPrefix,
      logIn,
      register,
      requestHumanStatus,
      requestPasswordReset,
      recordMarketingSmsConsent,
    }),
    [
      analyticsPrefix,
      logIn,
      register,
      requestHumanStatus,
      requestPasswordReset,
      recordMarketingSmsConsent,
    ],
  );
}

/** Supplies account actions without loading or publishing a session. */
export const AuthActionsProvider: React.FC<AuthActionsProviderProps> = ({ children, ...props }) => {
  const actions = useAuthActionsValue(props);
  return <AuthActionsContext.Provider value={actions}>{children}</AuthActionsContext.Provider>;
};
